<script setup lang="ts">
import StatusTag from '@taler/ui/StatusTag.vue'
import { useTemplateRef } from 'vue'

/**
 * A labelled text field or textarea, with an optional error message below. String in/out.
 * `labelSize: 'small'` is a locale sub-field inside a fieldset (muted, one step down).
 */
const props = withDefaults(
  defineProps<{
    modelValue: string
    label: string
    labelSize?: 'default' | 'small'
    type?: 'text' | 'textarea'
    // eslint-disable-next-line vue/require-default-prop -- must stay absent; no error shown when unset
    error?: string | undefined
    required?: boolean
    rows?: number
  }>(),
  { labelSize: 'default', type: 'text', required: false, rows: 3 },
)
const emit = defineEmits<{ 'update:modelValue': [value: string] }>()
defineOptions({ inheritAttrs: false })

const fieldRef = useTemplateRef<HTMLInputElement | HTMLTextAreaElement>('field')
defineExpose({ focus: () => fieldRef.value?.focus() })

function onInput(event: Event): void {
  emit('update:modelValue', (event.target as HTMLInputElement | HTMLTextAreaElement).value)
}

const LABEL_CLASS = {
  default: 'text-[length:var(--text-step-2)] text-[color:var(--color-text)]',
  small: 'text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]',
} as const

const FIELD_CLASS =
  'min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)] transition-colors duration-[var(--motion-fast)] placeholder:text-[color:var(--color-text-muted)] hover:border-[color:var(--color-text-muted)] focus-visible:border-[color:var(--color-focus)] focus-visible:shadow-[0_0_0_var(--ring-focus-width)_var(--color-focus)] focus-visible:outline-none aria-invalid:border-[color:var(--color-danger)] disabled:cursor-not-allowed disabled:opacity-60'
</script>

<template>
  <label class="flex flex-col gap-[var(--space-hair)]">
    <span :class="LABEL_CLASS[props.labelSize]">{{ label }}</span>
    <textarea
      v-if="type === 'textarea'"
      ref="field"
      v-bind="$attrs"
      :rows="rows"
      :required="required"
      :value="modelValue"
      :aria-invalid="error ? 'true' : undefined"
      :class="FIELD_CLASS"
      class="py-[var(--space-inline)]"
      @input="onInput"
    />
    <input
      v-else
      ref="field"
      v-bind="$attrs"
      type="text"
      :required="required"
      :value="modelValue"
      :aria-invalid="error ? 'true' : undefined"
      :class="FIELD_CLASS"
      @input="onInput"
    />
    <StatusTag v-if="error" tone="danger" role="alert">{{ error }}</StatusTag>
  </label>
</template>
